import React, { useState } from 'react';
import { Check, Minus } from 'lucide-react';
import SectionHeading from '../home/SectionHeading';
import Reveal from '../home/Reveal';

type TechniqueId = 'dtf' | 'embroidery' | 'htv';

interface Technique {
  id: TechniqueId;
  label: string;
  /** Column heading in the comparison table. */
  short: string;
  tagline: string;
  body: string;
  howItWorks: string;
  imageSrc: string;
  imageAlt: string;
}

const TECHNIQUES: Technique[] = [
  {
    id: 'dtf',
    label: 'Direct to Film (DTF)',
    short: 'DTF',
    tagline: 'A great solution for artwork with multiple colours and small detail.',
    body: 'DTF gives a similar finish to screen printing but without all the set up fees! DTF is smashing the garment customisation industry, making Direct to Garment and Sublimation printing a thing of the past.',
    howItWorks: 'DTF works by printing ink directly onto a film, then coating it with an adhesive powder, which can then be heat pressed onto virtually any fabric.',
    imageSrc: '/clothing-carousel/dunns-dtf-1300x867.jpg',
    imageAlt: 'DTF printed workwear for Dunns',
  },
  {
    id: 'embroidery',
    label: 'Embroidery',
    short: 'Embroidery',
    tagline: 'The best option for longevity, and a classic.',
    body: "A classic option for professional uniforms with small logos, names and text. It is particularly great for beanies and fleece fabric, which can't be customised any other way!",
    howItWorks: 'Embroidery does require some set up to digitise your logo into a suitable stitch file. Once your artwork is set up though, repeat orders are easy. Our commercial embroidery machine stitches 6 garments at a time with 1000s of stitches every minute.',
    imageSrc: '/clothing-carousel/Wildjac-embroidery-1300x867.jpg',
    imageAlt: 'Embroidered Wildjac logo',
  },
  {
    id: 'htv',
    label: 'Heat Transfer Vinyl (HTV)',
    short: 'HTV',
    tagline: 'Best suited to simple designs, logos and lettering.',
    body: 'HTV can be really fun and creative with loads of speciality finishes like glitter, neon and holographic! Artwork is cut, rather than printed, so the edges are super sharp and crisp. Colours are vibrant and have long-lasting protection from fading.',
    howItWorks: 'The artwork is cut from a roll of coloured vinyl with heat-sensitive glue on the reverse, then heat-pressed onto virtually any fabric.',
    imageSrc: '/clothing-carousel/Wildjac-Hoodie-HTV-2-1300x867.jpg',
    imageAlt: 'HTV vinyl logo on a Wildjac hoodie',
  },
];

/** `true` = yes, `false` = no, a string = yes with a condition. */
const FEATURES: { label: string; values: Record<TechniqueId, boolean | string> }[] = [
  { label: 'No set-up fee', values: { dtf: true, embroidery: 'From £5+VAT', htv: true } },
  { label: 'No minimum order', values: { dtf: true, embroidery: true, htv: true } },
  { label: 'Very small text and fine lines', values: { dtf: true, embroidery: false, htv: false } },
  { label: 'Gradients', values: { dtf: true, embroidery: false, htv: false } },
  { label: 'Photos', values: { dtf: true, embroidery: false, htv: false } },
  { label: 'Grunge / stamp effects', values: { dtf: true, embroidery: false, htv: false } },
  { label: 'Fluorescent / neon colours', values: { dtf: false, embroidery: true, htv: true } },
  { label: 'Metallic finishes', values: { dtf: false, embroidery: true, htv: true } },
  { label: 'Glitter + holographic', values: { dtf: false, embroidery: false, htv: true } },
];

const Cell: React.FC<{ value: boolean | string }> = ({ value }) => {
  if (value === true) return <Check className="mx-auto w-5 h-5 text-[#78BE20]" strokeWidth={2.5} aria-label="Yes" />;
  if (value === false) return <Minus className="mx-auto w-4 h-4 text-black/20" strokeWidth={2.5} aria-label="No" />;
  return <span className="font-poppins text-[10px] sm:text-xs font-medium leading-tight text-black/60 sm:whitespace-nowrap">{value}</span>;
};

/**
 * Three photo tiles over one comparison table. Hovering, focusing or tapping a
 * tile (or a column heading) highlights that technique's column.
 */
const CustomisationTechniques: React.FC = () => {
  const [active, setActive] = useState<TechniqueId | null>(null);

  const colClass = (id: TechniqueId) => (active === id ? 'bg-[#78BE20]/10' : '');

  return (
    <section className="w-full bg-white text-black py-20 md:py-24 px-6" onMouseLeave={() => setActive(null)}>
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <SectionHeading
            title="Types of customisation"
            subtitle="Our team can advise you on the best type of customisation for your garment and artwork."
            tone="green"
            size="compact"
            underline
          />
        </Reveal>

        {/* Tiles */}
        <Reveal delay={100} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TECHNIQUES.map((t, i) => {
            const on = active === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onMouseEnter={() => setActive(t.id)}
                onFocus={() => setActive(t.id)}
                onClick={() => setActive(t.id)}
                aria-pressed={on}
                className="group text-left flex flex-col outline-none focus-visible:ring-2 focus-visible:ring-[#78BE20]"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-black">
                  <img
                    src={t.imageSrc}
                    alt={t.imageAlt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <span
                    className="absolute inset-x-0 bottom-0 h-1 bg-[#78BE20] origin-left transition-transform duration-500"
                    style={{ transform: `scaleX(${on ? 1 : 0})` }}
                  />
                </div>
                <div className="pt-5">
                  <span className="font-poppins text-[11px] font-semibold tracking-[0.2em] text-[#78BE20]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-montserrat font-extrabold tracking-[-0.02em] text-xl md:text-2xl text-black leading-tight mt-1">
                    {t.label}
                  </h3>
                  <p className="font-poppins font-semibold text-[15px] text-black/90 mt-2">{t.tagline}</p>
                  <p className="font-poppins text-sm leading-6 text-black/65 mt-2">{t.body}</p>
                  <p className="font-poppins text-sm leading-6 text-black/65 mt-3">
                    <span className="font-semibold text-black">How it works: </span>
                    {t.howItWorks}
                  </p>
                </div>
              </button>
            );
          })}
        </Reveal>

        {/* Comparison */}
        <Reveal delay={150} className="mt-14">
          <h3 className="font-montserrat font-extrabold tracking-[-0.02em] text-2xl md:text-[28px] text-center">
            What each one can do
          </h3>
          <div className="mt-6 overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[320px] border-collapse font-poppins text-sm">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left font-semibold text-[10px] tracking-[0.2em] uppercase text-black/40 py-3 pr-3">
                    Feature
                  </th>
                  {TECHNIQUES.map((t) => (
                    <th
                      key={t.id}
                      scope="col"
                      onMouseEnter={() => setActive(t.id)}
                      className={`w-[15%] sm:w-[22%] py-3 px-1 sm:px-2 text-center font-montserrat font-extrabold text-[11px] sm:text-base transition-colors ${colClass(t.id)} ${
                        active === t.id ? 'text-[#5a9418]' : ''
                      }`}
                    >
                      {t.short}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f) => (
                  <tr key={f.label} className="border-b border-black/10">
                    <th scope="row" className="text-left font-normal text-black/75 py-3 pr-3">
                      {f.label}
                    </th>
                    {TECHNIQUES.map((t) => (
                      <td key={t.id} className={`py-3 px-1 sm:px-2 text-center transition-colors ${colClass(t.id)}`}>
                        <Cell value={f.values[t.id]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CustomisationTechniques;
